import { FC } from 'react';
import {
  BooleanField,
  ListContextProvider,
  TextField,
  useNotify,
  useRecordContext,
  useRefresh,
} from 'react-admin';

import {
  GetB2bWithdrawalsResponse,
  GetBoExternalWithdrawalResponse,
} from '@Common/types/bo_external/withdrawals';

import { assetTypeChoices } from '@sh4res/bo-common/choices/asset_types';
import { planCategoryChoices } from '@sh4res/bo-common/choices/plans';
import {
  b2bPaymentStatusChoices,
  b2bWithdrawalEarlyReasonChoices,
  b2bWithdrawalOrdersFlowChoices,
  b2bWithdrawalStatusChoices,
  b2bWithdrawalTypeChoices,
  bankAccountTypeChoices,
} from '@sh4res/bo-common/choices/withdrawals';
import {
  CancelButton,
  DatagridTyped,
  ShowFields,
  ShowSection,
  ShowWithNavigation,
} from '@sh4res/bo-common/components';
import { CurrencyField, IdField, SelectChipField, UTCDateField } from '@sh4res/bo-common/fields';
import { useCreatePromise, useRelatedWithdrawals } from '@sh4res/bo-common/hooks';
import { getErrorMessage } from '@sh4res/bo-common/utils';

import { withdrawalsListColumns } from '@Resources/withdrawals/WithdrawalsList';
import { logAction, reportError } from '@Utils/analytics';

const RelatedWithdrawalsSection: FC = () => {
  const record = useRecordContext<GetBoExternalWithdrawalResponse>();
  const { listContext, hasRelatedWithdrawals } = useRelatedWithdrawals<
    GetB2bWithdrawalsResponse[number]
  >({
    record,
    resource: 'withdrawals',
  });

  if (!hasRelatedWithdrawals) {
    return null;
  }

  return (
    <ShowSection label="resources.bo_common.b2b.withdrawals.sections.same_withdrawal_request">
      <ListContextProvider value={listContext}>
        <DatagridTyped<GetB2bWithdrawalsResponse[number]>
          bulkActionButtons={false}
          columnRegistry={withdrawalsListColumns}
          displayedColumns={['status', 'id', 'investing_product', 'early_withdraw_reason']}
          sortableColumns={[]}
        />
      </ListContextProvider>
    </ShowSection>
  );
};

const CancelWithdrawalButton: FC = () => {
  const record = useRecordContext<GetBoExternalWithdrawalResponse>();
  const [cancelWithdrawal] = useCreatePromise<{ id: number }>();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) {
    return null;
  }

  const handleCancel = async () => {
    logAction('Withdrawal cancellation requested', { withdrawalId: record.id });

    try {
      await cancelWithdrawal('withdrawals/cancel', { data: { id: record.id } });
      notify('resources.bo_common.b2b.withdrawals.cancel.success', { type: 'success' });
      refresh();
    } catch (error) {
      reportError('Withdrawal cancellation failed', error, { withdrawalId: record.id });
      notify('resources.bo_common.b2b.withdrawals.cancel.failure', {
        type: 'warning',
        messageArgs: { error: getErrorMessage(error) },
        multiLine: true,
      });
    }
  };

  return (
    <CancelButton
      data-testid="BoExternalWithdrawalsShow_CancelButton"
      label="resources.bo_common.b2b.withdrawals.cancel.label"
      onClick={handleCancel}
      disabled={record.status === 'cancelled'}
      permissions={{ resource: 'withdrawals/cancel', action: 'create' }}
    />
  );
};

export const WithdrawalsShow: FC = () => {
  const testId = 'BoExternalWithdrawalsShow';

  return (
    <ShowWithNavigation
      actions={false}
      titleActions={<CancelWithdrawalButton />}
      title="resources.bo_common.b2b.withdrawals.title.show">
      <RelatedWithdrawalsSection />
      <ShowSection label="resources.bo_common.b2b.withdrawals.sections.details">
        <ShowFields<GetBoExternalWithdrawalResponse>
          fields={{
            id: <IdField reference="withdrawals" data-testid={`${testId}_IdField`} />,
            employee_id: (
              <IdField reference="employees" data-testid={`${testId}_EmployeeIdField`} />
            ),
            contract_id: (
              <IdField reference="contracts" data-testid={`${testId}_ContractIdField`} />
            ),

            investing_product: (
              <SelectChipField
                choices={planCategoryChoices}
                data-testid={`${testId}_InvestingProductField`}
              />
            ),
            assets_class: (
              <SelectChipField
                choices={assetTypeChoices}
                data-testid={`${testId}_AssetsClassField`}
              />
            ),
            account_type: (
              <SelectChipField
                choices={bankAccountTypeChoices}
                data-testid={`${testId}_AccountTypeField`}
              />
            ),
            sort_code: <TextField data-testid={`${testId}_SortCodeField`} />,
            account_number: <TextField data-testid={`${testId}_AccountNumberField`} />,
            bank_country: <TextField data-testid={`${testId}_BankCountryField`} />,
            status: (
              <SelectChipField
                choices={b2bWithdrawalStatusChoices}
                data-testid={`${testId}_StatusField`}
              />
            ),
            type: (
              <SelectChipField
                choices={b2bWithdrawalTypeChoices}
                data-testid={`${testId}_TypeField`}
              />
            ),
            orders_flow: (
              <SelectChipField
                choices={b2bWithdrawalOrdersFlowChoices}
                data-testid={`${testId}_OrdersFlowField`}
              />
            ),
            is_planned_withdrawal: (
              <BooleanField data-testid={`${testId}_IsPlannedWithdrawalField`} />
            ),
            gross_amount: (
              <CurrencyField currency="currency" data-testid={`${testId}_GrossAmountField`} />
            ),
            net_amount: (
              <CurrencyField currency="currency" data-testid={`${testId}_NetAmountField`} />
            ),
            tax_amount: (
              <CurrencyField currency="currency" data-testid={`${testId}_TaxAmountField`} />
            ),
            initial_amount: (
              <CurrencyField currency="currency" data-testid={`${testId}_InitialAmountField`} />
            ),
            paid_at: <UTCDateField data-testid={`${testId}_PaidAtField`} />,
            payment_status: (
              <SelectChipField
                choices={b2bPaymentStatusChoices}
                data-testid={`${testId}_PaymentStatusField`}
              />
            ),
            created_at: <UTCDateField showTime data-testid={`${testId}_CreatedAtField`} />,
            updated_at: <UTCDateField showTime data-testid={`${testId}_UpdatedAtField`} />,
            correlation_id: <TextField data-testid={`${testId}_CorrelationIdField`} />,
          }}
        />
      </ShowSection>
      <ShowSection label="resources.bo_common.b2b.withdrawals.sections.early">
        <ShowFields<GetBoExternalWithdrawalResponse>
          fields={{
            early_withdraw_reason: (
              <SelectChipField
                choices={b2bWithdrawalEarlyReasonChoices}
                data-testid={`${testId}_EarlyWithdrawReasonField`}
              />
            ),
            early_withdraw_event_date: (
              <UTCDateField data-testid={`${testId}_EarlyWithdrawEventDateField`} />
            ),
          }}
        />
      </ShowSection>
    </ShowWithNavigation>
  );
};
